import type { ButtonHTMLAttributes, ComponentProps, ReactNode } from 'react';
import { Link } from 'react-router';
import { formatInteger } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import type { ExternalSourceInfo } from '@brimo/shared';
import { Eyebrow, Tooltip } from '../../ds/components';
import { sourceBadgeLabel, sourceBadgeTooltip } from './availability';
import type { DocumentRow } from '../contacts/types';
import { fileMeta } from './format';
import type { Tone } from './types';
import styles from './bits.module.css';

/** The rectangular status pill of the lists and records (4/9, `--radius-sm`, 11 px medium). */
export function StatusPill({
  tone,
  children,
  className,
  unit = false,
}: {
  tone: Tone;
  children: ReactNode;
  className?: string | undefined;
  /** Units: "Frei" in `--stone-700` on `--sand-200`, "Reserviert" with the accent border. */
  unit?: boolean | undefined;
}) {
  const toneClass = {
    success: styles.pillSuccess,
    warning: styles.pillWarning,
    danger: styles.pillDanger,
    info: styles.pillInfo,
    accent: styles.pillAccent,
    neutral: unit ? styles.pillUnitFree : styles.pillNeutral,
  }[tone];
  return <span className={clsx(styles.pill, toneClass, className)}>{children}</span>;
}

export interface Crumb {
  label: string;
  href?: string | undefined;
}

/** "Objekte › Quartier Lindenhof › WE 2.05" (11 px, `›` separators, the current item in `--text-body`). */
export function Crumbs({
  items,
  separator = '›',
  className,
}: {
  items: Crumb[];
  separator?: '›' | '/' | undefined;
  className?: string | undefined;
}) {
  return (
    <nav
      className={clsx(styles.crumbs, separator === '/' && styles.crumbsSlash, className)}
      aria-label="Brotkrumen"
    >
      {items.map((item, index) => (
        <span key={`${item.label}-${index}`} className={styles.crumbItem}>
          {index > 0 ? (
            <span className={styles.crumbSeparator} aria-hidden="true">
              {separator}
            </span>
          ) : null}
          {item.href ? (
            <Link to={item.href} className={styles.crumbLink}>
              {item.label}
            </Link>
          ) : (
            <span
              className={styles.crumbCurrent}
              aria-current={index === items.length - 1 ? 'page' : undefined}
            >
              {item.label}
            </span>
          )}
        </span>
      ))}
    </nav>
  );
}

/** The three-segment sales bar (verkauft · reserviert · frei) with its legend. */
export function SalesBar({
  sold,
  reserved,
  available,
  total,
  exclusive = 0,
  notReleased = 0,
  height = 6,
  legend = 'grid',
}: {
  sold: number;
  reserved: number;
  available: number;
  total: number;
  /** Exclusively reserved in TeamProQ; shown only when > 0. */
  exclusive?: number | undefined;
  /** Not released for sale; shown only when > 0. */
  notReleased?: number | undefined;
  height?: 5 | 6 | undefined;
  /** `grid`: the tile legend with dots; `line`: "14 verkauft · 6 res. · 14 frei" (list rows). */
  legend?: 'grid' | 'line' | undefined;
}) {
  const base = total > 0 ? total : 1;
  const pct = (n: number) => `${Math.round((n / base) * 100)}%`;
  const label = [
    `${formatInteger(sold)} verkauft`,
    `${formatInteger(reserved)} reserviert`,
    ...(exclusive > 0 ? [`${formatInteger(exclusive)} exklusiv`] : []),
    `${formatInteger(available)} frei`,
    ...(notReleased > 0 ? [`${formatInteger(notReleased)} nicht freigegeben`] : []),
  ].join(', ');
  return (
    <>
      <div className={clsx(styles.bar, height === 5 && styles.barThin)} role="img" aria-label={label}>
        <span className={styles.barSold} style={{ width: pct(sold) }} />
        <span className={styles.barReserved} style={{ width: pct(reserved) }} />
        {exclusive > 0 ? <span className={styles.barExclusive} style={{ width: pct(exclusive) }} /> : null}
        <span className={styles.barFree} style={{ width: pct(available) }} />
        {notReleased > 0 ? (
          <span className={styles.barNotReleased} style={{ width: pct(notReleased) }} />
        ) : null}
      </div>
      {legend === 'grid' ? (
        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <span className={clsx(styles.legendDot, styles.legendDotSold)} />
            {formatInteger(sold)} verkauft
          </span>
          <span className={styles.legendItem}>
            <span className={clsx(styles.legendDot, styles.legendDotReserved)} />
            {formatInteger(reserved)} reserviert
          </span>
          {exclusive > 0 ? (
            <span className={styles.legendItem}>
              <span className={clsx(styles.legendDot, styles.legendDotExclusive)} />
              {formatInteger(exclusive)} exklusiv
            </span>
          ) : null}
          <span className={styles.legendItem}>
            <span className={clsx(styles.legendDot, styles.legendDotFree)} />
            {formatInteger(available)} frei
          </span>
          {notReleased > 0 ? (
            <span className={clsx(styles.legendItem, styles.legendItemWide)}>
              <span className={clsx(styles.legendDot, styles.legendDotNotReleased)} />
              {formatInteger(notReleased)} nicht freigegeben
            </span>
          ) : null}
        </div>
      ) : (
        <div className={styles.legendLine}>
          {formatInteger(sold)} verkauft · {formatInteger(reserved)} res.
          {exclusive > 0 ? ` · ${formatInteger(exclusive)} exkl.` : ''} · {formatInteger(available)} frei
          {notReleased > 0 ? ` · ${formatInteger(notReleased)} n. freig.` : ''}
        </div>
      )}
    </>
  );
}

/** Label (11 px subtle) over value (13 px strong) — the data cards of the records. */
/** Eyebrow as the frames' bare `<span>` inside a card (24 px line box). */
export function EyebrowInline({ className, ...rest }: ComponentProps<typeof Eyebrow>) {
  return <Eyebrow className={clsx(styles.eyebrowInline, className)} {...rest} />;
}

export function FieldPair({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className={styles.field}>
      <div className={styles.fieldLabel}>{label}</div>
      <div className={styles.fieldValue}>{value}</div>
    </div>
  );
}

/** Card head: eyebrow left, something small right (count, note, button). */
export function CardHead({
  eyebrow,
  end,
  id,
}: {
  eyebrow: string;
  end?: ReactNode | undefined;
  id?: string | undefined;
}) {
  return (
    <div className={styles.cardHead}>
      <Eyebrow id={id}>{eyebrow}</Eyebrow>
      {end !== undefined ? <span className={styles.cardHeadEnd}>{end}</span> : null}
    </div>
  );
}

/** The small hairline button of card heads ("Bearbeiten", 5/10, 11 px). */
export function TinyButton({ className, type = 'button', ...rest }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type={type} className={clsx('pressable', styles.tinyButton, className)} {...rest} />;
}

/** The dashed accent upload button under the document rows. */
export function DashedButton({
  className,
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type={type} className={clsx('pressable', styles.dashedButton, className)} {...rest} />;
}

export interface DocumentRowItem {
  key: string;
  name: string;
  meta: string;
  tone?: 'default' | 'missing' | 'accent' | undefined;
  href?: string | undefined;
}

/** Rows of the "Objektunterlagen" / "Unterlagen zur Einheit" cards. */
export function DocumentRows({
  items,
  emptyText,
}: {
  items: DocumentRowItem[];
  emptyText?: string | undefined;
}) {
  if (items.length === 0) return emptyText ? <p className={styles.docsEmpty}>{emptyText}</p> : null;
  return (
    <div className={styles.docRows}>
      {items.map((item) => {
        const inner = (
          <>
            <span className={styles.docName} title={item.name}>
              {item.name}
            </span>
            <span
              className={clsx(styles.docMeta, item.tone === 'accent' && styles.docMetaAccent)}
              title={item.meta}
            >
              {item.meta}
            </span>
          </>
        );
        const className = clsx(styles.docRow, item.tone === 'missing' && styles.docRowMissing);
        return item.href ? (
          <a key={item.key} className={className} href={item.href} target="_blank" rel="noopener">
            {inner}
          </a>
        ) : (
          <div key={item.key} className={className}>
            {inner}
          </div>
        );
      })}
    </div>
  );
}

export function documentRowItem(row: DocumentRow): DocumentRowItem {
  const item: DocumentRowItem = { key: row.id, name: row.name, meta: fileMeta(row) };
  if (row.canOpen && row.fileId) item.href = `/api/files/${row.fileId}`;
  return item;
}

/** „Aus TeamProQ" next to the status of an imported property or unit; the tooltip names the last sync. */
export function SourceBadge({ external }: { external: NonNullable<ExternalSourceInfo> }) {
  return (
    <Tooltip content={sourceBadgeTooltip(external)}>
      <span className={styles.sourceBadge} tabIndex={0}>
        {sourceBadgeLabel(external)}
      </span>
    </Tooltip>
  );
}
